import 'plyr/src/sass/plyr.scss'

import type { AssemblyResult } from '@uppy/transloadit'
import type { HlsConfig } from 'hls.js'

export interface MediaElementifyOptions {
  adaptiveVideoResults?: AssemblyResult[]
}

// Helper functions
function getSourceUrl(element: HTMLMediaElement): string | null {
  return element.getAttribute('src') || element.querySelector('source')?.getAttribute('src') || null
}

function getSourceType(element: HTMLMediaElement): string | null {
  return (
    element.getAttribute('type') || element.querySelector('source')?.getAttribute('type') || null
  )
}

function findClosestElement(element: Element, selector: string): Element | null {
  return element.closest(selector)
}

function showPlayerPlaybackFailure(_player: Plyr, element: HTMLMediaElement, sourceType: string) {
  const container = findClosestElement(element, '.FilePreviewContainer')
  const alert = container?.querySelector('.playback-alert')

  // If we're in demo.html - we have .FilePreviewContainer.
  if (alert && alert instanceof HTMLElement) {
    const code = document.createElement('code')
    code.innerText = sourceType
    alert.append(' Problematic was: ', code, '. ')
  }
}

async function createPlayer(element: HTMLMediaElement, options?: MediaElementifyOptions) {
  const sourceUrl = getSourceUrl(element)
  const sourceType = getSourceType(element)

  if (!sourceUrl) {
    throw new Error('No source URL found')
  }

  if (!sourceType) {
    throw new Error('No source type found')
  }

  const { default: Plyr } = await import('plyr')

  const isHls = sourceUrl.endsWith('.m3u8')
  const isDash = sourceUrl.match('.mpd')
  const autoplay = false

  // https://codepen.io/sampotts/pen/JKEMqB
  if (isHls) {
    const { Hls, getRewritingHlsLoader } = await import('./mediaelementify/hls.ts')
    if (Hls.isSupported()) {
      const config: Partial<HlsConfig> = {}

      if (options?.adaptiveVideoResults) {
        config.loader = getRewritingHlsLoader(options.adaptiveVideoResults)
      }

      const hls = new Hls(config)

      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.MEDIA_ERROR: {
              hls.recoverMediaError()
              break
            }
            case Hls.ErrorTypes.NETWORK_ERROR: {
              // All retries and media options have been exhausted.
              // Immediately trying to restart loading could cause loop loading.
              // Consider modifying loading policies to best fit your asset and network
              // conditions (manifestLoadPolicy, playlistLoadPolicy, fragLoadPolicy).
              break
            }
            default: {
              // cannot recover
              hls.destroy()
              break
            }
          }
        }
      })

      hls.loadSource(sourceUrl)
      hls.attachMedia(element)
    }
  }

  // https://codepen.io/sampotts/pen/BzpJXN
  if (isDash) {
    const { MediaPlayer, createRewritingDashLoaderFactory } = await import(
      './mediaelementify/dash.ts'
    )
    const dash = MediaPlayer().create()
    if (options?.adaptiveVideoResults) {
      dash.extend(
        'HTTPLoader',
        createRewritingDashLoaderFactory(options.adaptiveVideoResults),
        false,
      )
    }
    dash.initialize(element, sourceUrl, autoplay)
  }

  // https://github.com/sampotts/plyr#options
  const player = new Plyr(element, {
    controls: ['play', 'progress', 'current-time'],
  })

  if (!isDash && !isHls) {
    if (!player.supports(sourceType)) {
      showPlayerPlaybackFailure(player, element, sourceType)
    }
  }
}

function initPlayer(element: HTMLMediaElement, options?: MediaElementifyOptions) {
  // Avoid that the player is initialized multiple times if
  // TiCommon.mediaelementify is called more than once. This would
  // lead to duplicated controls as described in
  // https://github.com/transloadit/api2/issues/760.
  if (element.dataset.isPlyrInitialized === 'true') {
    return
  }
  element.dataset.isPlyrInitialized = 'true'

  createPlayer(element, options).catch((error) => {
    // createPlayer only fails if we cannot load a bundle, which would be a bug …
    // explicitly not handling this rejection so sentry can notify us.
    throw error
  })
}

const mediaelementify = (element?: HTMLMediaElement, options: MediaElementifyOptions = {}) => {
  if (element) {
    return initPlayer(element, options)
  }
  document.querySelectorAll<HTMLMediaElement>('.plyr-player').forEach((el) => {
    initPlayer(el, options)
  })
}

export default mediaelementify
